import { ReactNode, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import classNames from "clsx";
import { useDebounce } from "usehooks-ts";
import { Theme } from "react-migration/lib/theme/Theme";

export interface DeprecatedTooltipProps {
  children: ReactNode;
  title?: ReactNode;
  description?: string | JSX.Element;
  className?: string;
  descriptionClassName?: string;
  childrenWrapperClassName?: string;
  placement?: "top" | "left" | "right" | "bottom" | "top-left" | "top-right" | "bottom-left" | "bottom-right";
  showArrow?: boolean;
  theme?: Theme;
  disabled?: boolean;
  widthClass?: string;
  delayMs?: number;
  xOffset?: number;
  yOffset?: number;
  hideTooltip?: boolean;
  relativePositioning?: boolean;
}

const SPACING = 10;

/**
 * @deprecated Formally just "Tooltip". This component has been deprecated, and replaced with the "Radix Powered" "Tooltip" component (components/Tooltip).
 *
 * Please refrain from using this component, unless:
 *   * You don't have time to refactor.
 *   * You are aware that the new Tooltip Component will have problems in production (typically if you are rendering the component directly in Vue).
 */
export function DeprecatedTooltip({
  children,
  title,
  description,
  className,
  widthClass,
  descriptionClassName = "whitespace-pre items-center",
  childrenWrapperClassName,
  placement = "top",
  showArrow = true,
  theme = Theme.Light,
  disabled = false,
  delayMs = 0,
  xOffset = 0,
  yOffset = 0,
  hideTooltip = false,
  relativePositioning,
}: DeprecatedTooltipProps) {
  const [isVisible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const relativePosition = useMemo(() => {
    switch (placement) {
      case "top":
        return {
          top: 0 - SPACING,
          left: 50,
        };
      case "top-right":
        return {
          top: 0 - SPACING,
          left: 100,
        };

      case "top-left":
        return {
          top: 0 - SPACING,
          left: 20,
        };

      case "bottom":
        return {
          top: 100 + SPACING,
          left: 50,
        };
      case "bottom-right":
        return {
          top: 100 + SPACING,
          left: 100,
        };

      case "bottom-left":
        return {
          top: 100 + SPACING,
          left: 20,
        };
      case "right":
        return {
          top: 50,
          left: 100 + SPACING,
        };
      case "left":
        return {
          top: 50,
          left: 0 - SPACING,
        };
    }
  }, [placement]);

  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (disabled) {
      return;
    }
    if (tooltipRef.current) {
      const { top, left, width, height } = tooltipRef.current.getBoundingClientRect();

      setVisible(true);
      switch (placement) {
        case "top":
        case "top-right":
          setPosition({ top: top - SPACING + yOffset, left: left + width / 2 + xOffset });
          break;
        case "top-left":
          setPosition({ top: top - SPACING + yOffset, left: left + width / 2 + 24 + xOffset });
          break;
        case "bottom":
        case "bottom-right":
          setPosition({ top: top + height + SPACING + yOffset, left: left + width / 2 + xOffset });
          break;
        case "bottom-left":
          setPosition({
            top: top + height + SPACING + yOffset,
            left: left + width / 2 + 24 + xOffset,
          });
          break;
        case "right":
          setPosition({
            top: top + height / 2 - 2 + yOffset,
            left: left + width + SPACING + xOffset,
          });
          break;
        case "left":
          setPosition({ top: top + height / 2 - 2 + yOffset, left: left - SPACING + xOffset });
          break;
      }
    }
  };

  const debouncedIsVisible = useDebounce(isVisible, delayMs);
  const showTooltip = isVisible && debouncedIsVisible;

  if (hideTooltip) {
    return <>{children}</>;
  }
  return (
    <div
      className={clsx(
        {
          "inline-block": !className || !className.includes("block"),
          relative: relativePositioning,
        },
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => {
        setVisible(false);
      }}
      data-testid="tooltip-wrapper"
      ref={tooltipRef}
    >
      <div className={classNames(childrenWrapperClassName ?? "block")}>{children}</div>
      <div
        style={{
          top: relativePositioning ? `${relativePosition?.top}%` : position.top,
          left: relativePositioning ? `${relativePosition?.left}%` : position.left,
          display: showTooltip ? undefined : "none",
        }}
        data-testid="tooltip-content"
        className={clsx("fixed z-9998", {
          "-translate-y-full -translate-x-1/2": placement === "top",
          "-translate-y-full -translate-x-full": placement === "top-left",
          "-translate-y-full -translate-x-6": placement === "top-right",
          "translate-y-0 -translate-x-1/2": placement === "bottom",
          "translate-y-0 -translate-x-full": placement === "bottom-left",
          "translate-y-0 -translate-x-6": placement === "bottom-right",
          "-translate-x-full -translate-y-1/2": placement === "left",
          "translate-x-0 -translate-y-1/2": placement === "right",
          absolute: relativePositioning,
        })}
      >
        {/* Tooltip body */}
        <div
          className={clsx(
            "rounded-sm px-3",
            widthClass && widthClass,
            title && description ? "py-3" : "py-2",
            theme === Theme.Dark ? "bg-neutral-700" : "bg-white",
            {
              "border border-border-action": !showArrow && theme === Theme.Light,
            }
          )}
        >
          {title && (
            <div
              className={clsx(
                "text-xs",
                "font-semibold",
                theme === Theme.Light ? "text-text-base-primary" : "text-white"
              )}
            >
              {title}
            </div>
          )}
          {description && (
            <div
              className={clsx(
                "text-xs",
                "font-normal",
                theme === Theme.Light ? "text-text-base-tertiary" : "text-neutral-200",
                {
                  "mt-1": !!title,
                  "text-center": !title,
                }
              )}
            >
              <span className={descriptionClassName}>{description}</span>
            </div>
          )}
        </div>

        {/* Tooltip triangle */}
        {showArrow && (
          <div
            className={clsx(
              "absolute w-0 h-0",
              {
                "border-neutral-700": theme === Theme.Dark,
                "border-white": theme === Theme.Light,
                "border-t": ["top", "top-left", "top-right"].includes(placement),
                "border-b": ["bottom", "bottom-left", "bottom-right"].includes(placement),
                "border-l": placement === "left",
                "border-r": placement === "right",
              },
              {
                "left-1/2 bottom-0 translate-y-full -translate-x-1/2 border-x-transparent border-x-8 border-t-8":
                  placement === "top",
                "right-6 bottom-0 translate-y-full translate-x-1/2 border-x-transparent border-x-8 border-t-8":
                  placement === "top-left",
                "left-6 bottom-0 translate-y-full -translate-x-1/2 border-x-transparent border-x-8 border-t-8":
                  placement === "top-right",
                "left-1/2 top-0 -translate-y-full -translate-x-1/2 border-x-transparent border-x-8 border-b-8":
                  placement === "bottom",
                "right-6 top-0 -translate-y-full translate-x-1/2 border-x-transparent border-x-8 border-b-8":
                  placement === "bottom-left",
                "left-6 top-0 -translate-y-full -translate-x-1/2 border-x-transparent border-x-8 border-b-8":
                  placement === "bottom-right",
                "right-0 top-1/2 translate-x-full -translate-y-1/2 border-y-transparent border-y-8 border-l-8":
                  placement === "left",
                "left-0 top-1/2 -translate-x-full -translate-y-1/2 border-y-transparent border-y-8 border-r-8":
                  placement === "right",
              }
            )}
          />
        )}
      </div>
    </div>
  );
}
